import { zodResolver } from '@hookform/resolvers/zod'
import {
  Clock,
  Globe,
  Hash,
  ImagePlay,
  Layers,
  ListOrdered,
  Minus,
  Plus,
  Sparkles,
  Wand2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import { SuggestPromptButton } from '@/features/factory/components/SuggestPromptButton'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { useStartRanked, useSuggestPrompt, type RankedInput } from '@/api/endpoints/factories'
import { useThemes, type Theme } from '@/api/endpoints/themes'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { NarratorPicker } from '../_shared/NarratorPicker'
import { PresetBar } from '../_shared/PresetBar'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'

const FIELD_LABELS: Record<string, string> = {
  theme: 'Tema',
  count: 'Quantidade',
  language: 'Idioma',
  custom_prompt: 'Prompt',
  item_count: 'Itens no ranking',
  target_duration: 'Duração',
  stock_source: 'Vídeo de fundo',
  tts_voice_pt: 'Narrador (PT)',
  tts_voice_en: 'Narrador (EN)',
}

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en' | 'both'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

type StockSourceKey = 'pexels' | 'pixabay'
const STOCK_SOURCES: ReadonlyArray<SegmentedOption<StockSourceKey>> = [
  { value: 'pexels', label: 'Pexels' },
  { value: 'pixabay', label: 'Pixabay' },
]

// Common duration presets matching the backend's target_duration knob.
// 60s default = Top 10 at ~6s/item (the canonical countdown shape on
// Shorts). 45s pairs with Top 5/7 for tighter pacing; 90s gives Top 10
// room to breathe (~9s/item) when the items have richer narration.
type DurationKey = '45' | '60' | '75' | '90'
const DURATIONS: ReadonlyArray<SegmentedOption<DurationKey>> = [
  { value: '45', label: '45s' },
  { value: '60', label: '60s' },
  { value: '75', label: '75s' },
  { value: '90', label: '90s' },
]

const META_THEME_OPTIONS = [{ value: 'custom', label: 'Prompt customizado' }] as const

const schema = z
  .object({
    theme: z.string().min(1, 'Escolha um tema ou prompt'),
    count: z.number().int().min(1).max(10),
    language: z.enum(['pt-br', 'en', 'both']),
    item_count: z.number().int().min(3).max(15),
    target_duration: z.number().int().min(20).max(120),
    custom_prompt: z.string().trim().optional(),
    stock_source: z.enum(['pexels', 'pixabay']).optional(),
    tts_voice_pt: z.string().optional(),
    tts_voice_en: z.string().optional(),
    llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
  })
  // Empty custom_prompt is valid — backend auto-generates via suggest_prompt.
  .refine(
    (v) => {
      if (v.theme !== 'custom') return true
      const len = v.custom_prompt?.length ?? 0
      return len === 0 || len >= 10
    },
    {
      path: ['custom_prompt'],
      message: 'Descreva com pelo menos 10 caracteres, ou deixe em branco para gerar um automaticamente.',
    },
  )

type FormValues = z.infer<typeof schema>

const FORM_DEFAULTS: FormValues = {
  theme: 'ranked-richest_athletes',
  count: 1,
  language: 'pt-br',
  item_count: 10,
  target_duration: 60,
  custom_prompt: '',
}

function formToPayload(values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    theme: values.theme,
    language: values.language,
    item_count: values.item_count,
    target_duration: values.target_duration,
    custom_prompt: values.theme === 'custom' && values.custom_prompt ? values.custom_prompt : '',
  }
  if (values.stock_source) payload.stock_source = values.stock_source
  if (values.tts_voice_pt) payload.tts_voice_pt = values.tts_voice_pt
  if (values.tts_voice_en) payload.tts_voice_en = values.tts_voice_en
  return payload
}

function payloadToForm(params: Record<string, unknown>): FormValues {
  const v: FormValues = {
    ...FORM_DEFAULTS,
    theme: (params.theme as string | undefined) ?? FORM_DEFAULTS.theme,
    language:
      ((params.language as FormValues['language'] | undefined) ?? null) ?? FORM_DEFAULTS.language,
    item_count: (params.item_count as number | undefined) ?? FORM_DEFAULTS.item_count,
    target_duration:
      (params.target_duration as number | undefined) ?? FORM_DEFAULTS.target_duration,
    custom_prompt: (params.custom_prompt as string | undefined) ?? '',
  }
  if (params.stock_source === 'pexels' || params.stock_source === 'pixabay') {
    v.stock_source = params.stock_source
  }
  if (params.tts_voice_pt) v.tts_voice_pt = params.tts_voice_pt as string
  if (params.tts_voice_en) v.tts_voice_en = params.tts_voice_en as string
  return v
}

export function RankedForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const { data: allThemes = [], isPending: themesPending } = useThemes()
  // Filter themes client-side by slug prefix — themes endpoint returns
  // every JSON in config/themes/.
  const themes = allThemes.filter((t: Theme) => t.slug.startsWith('ranked-'))

  const startMutation = useStartRanked()
  const suggestPrompt = useSuggestPrompt()

  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      theme: themes[0]?.slug ?? 'ranked-richest_athletes',
      count: 1,
      language: 'pt-br',
      item_count: 10,
      target_duration: 60,
      custom_prompt: '',
      llm_provider: 'auto',
    },
  })

  const theme = form.watch('theme')
  const count = form.watch('count')
  const language = form.watch('language')
  const itemCount = form.watch('item_count')
  const targetDuration = form.watch('target_duration')
  const stockSource = form.watch('stock_source')
  const voicePt = form.watch('tts_voice_pt')
  const voiceEn = form.watch('tts_voice_en')
  const llmProvider = form.watch('llm_provider')

  const onSubmit = form.handleSubmit((values) => {
    const payload: RankedInput = {
      theme: values.theme,
      channel: channel || null,
      count: values.count,
      language: values.language,
      item_count: values.item_count,
      target_duration: values.target_duration,
      force: false,
      custom_prompt: values.custom_prompt ?? '',
      stock_source: values.stock_source ?? null,
      tts_voice_pt: values.tts_voice_pt || null,
      tts_voice_en: values.tts_voice_en || null,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <ListOrdered aria-hidden /> Ranked · countdown #{itemCount} → #1
          </div>
          <h1 className={styles.title}>Qual ranking você quer hoje?</h1>
          <p className={styles.subtitle}>
            Escolha um tema; o Prism gera o Top {itemCount} com cards de posição animados, narração ancorada em fontes confiáveis e fotos via Wikimedia Commons quando possível.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<FormValues>
              factory="ranked"
              channel={channel}
              formValues={form.watch()}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={(values) => {
                form.reset(values)
              }}
            />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<Hash />} label="Tema" htmlFor="ranked-theme">
              <SelectRoot
                value={theme}
                onValueChange={(v) => {
                  form.setValue('theme', v, { shouldValidate: true })
                }}
              >
                <SelectTrigger id="ranked-theme" aria-label="Tema">
                  <SelectValue placeholder={themesPending ? 'Carregando...' : 'Escolha um tema'} />
                </SelectTrigger>
                <SelectContent>
                  {META_THEME_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                  {themes.map((t: Theme) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
              {form.formState.errors.theme ? (
                <span className={styles.errorText}>{form.formState.errors.theme.message}</span>
              ) : null}
            </Row>

            {theme === 'custom' ? (
              <Row icon={<Wand2 />} label="Prompt" htmlFor="ranked-prompt">
                <textarea
                  id="ranked-prompt"
                  className={cn(styles.textarea, form.formState.errors.custom_prompt && styles.textareaError)}
                  placeholder="Ex: 10 países com maior número de fusos horários"
                  rows={3}
                  {...form.register('custom_prompt')}
                />
                <SuggestPromptButton
                  factory="ranked"
                  language={language}
                  channel={channel}
                  pending={suggestPrompt.isPending}
                  error={suggestPrompt.error}
                  onClick={() => {
                    suggestPrompt.mutate(
                      { factory: 'ranked', language, channel: channel || null },
                      {
                        onSuccess: (prompt) => {
                          form.setValue('custom_prompt', prompt, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        },
                      },
                    )
                  }}
                />
                {form.formState.errors.custom_prompt ? (
                  <span className={styles.errorText}>{form.formState.errors.custom_prompt.message}</span>
                ) : null}
              </Row>
            ) : null}

            <Row icon={<ListOrdered />} label="Itens no ranking">
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={itemCount <= 3}
                  onClick={() => {
                    form.setValue('item_count', Math.max(3, itemCount - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{itemCount}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={itemCount >= 15}
                  onClick={() => {
                    form.setValue('item_count', Math.min(15, itemCount + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            <Row icon={<Clock />} label="Duração">
              <Segmented<DurationKey>
                options={DURATIONS}
                value={String(targetDuration) as DurationKey}
                onValueChange={(v) => {
                  form.setValue('target_duration', Number(v))
                }}
                aria-label="Duração alvo do vídeo"
              />
            </Row>

            <Row icon={<Layers />} label="Quantidade">
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={count <= 1}
                  onClick={() => {
                    form.setValue('count', Math.max(1, count - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{count}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={count >= 10}
                  onClick={() => {
                    form.setValue('count', Math.min(10, count + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            {advancedOpen ? (
              <div className={styles.advanced}>
                <AdvSection icon={<Globe />} label="Idioma">
                  <Segmented<'pt-br' | 'en' | 'both'>
                    options={LANGUAGES}
                    value={language}
                    onValueChange={(v) => {
                      form.setValue('language', v)
                    }}
                    aria-label="Idioma"
                  />
                </AdvSection>

                <AdvSection icon={<ImagePlay />} label="Vídeo de fundo">
                  <Segmented<StockSourceKey>
                    options={STOCK_SOURCES}
                    value={stockSource ?? 'pexels'}
                    onValueChange={(v) => {
                      form.setValue('stock_source', v)
                    }}
                    aria-label="Provider de stock-video"
                  />
                </AdvSection>

                <NarratorPicker
                  language={language}
                  voicePt={voicePt ?? ''}
                  voiceEn={voiceEn ?? ''}
                  onVoicePtChange={(v) => {
                    form.setValue('tts_voice_pt', v)
                  }}
                  onVoiceEnChange={(v) => {
                    form.setValue('tts_voice_en', v)
                  }}
                />

                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="ranked" />
            <button type="submit" className={styles.generateBtn} disabled={!channel || startMutation.isPending}>
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}

